import * as Sentry from '@sentry/react';

const IGNORED_ERRORS: (string | RegExp)[] = [
  // Browser extensions inject scripts that throw errors we can't control
  /extensions\//i,
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  // ResizeObserver noise — benign browser implementation detail
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  // Network errors with no actionable stack trace
  'Network Error',
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource',
  // Cancelled requests (user navigated away)
  'AbortError',
  'The operation was aborted',
  'signal is aborted without reason',
  // Common non-actionable browser errors
  'Non-Error promise rejection captured',
];

const STATIC_ASSET_PATTERN = /\.(js|css|png|jpg|svg|woff2?|ico|map)(\?|$)/;

const MAPBOX_GEOCODING_URL_PREFIX = 'https://api.mapbox.com/geocoding/';

export function isIgnoredError(event: Sentry.Event): boolean {
  const message = event.message || '';
  const exceptionValues = event.exception?.values || [];

  for (const pattern of IGNORED_ERRORS) {
    if (typeof pattern === 'string') {
      if (message.includes(pattern) || exceptionValues.some((v) => v.value?.includes(pattern))) {
        return true;
      }
    } else {
      if (pattern.test(message) || exceptionValues.some((v) => v.value && pattern.test(v.value))) {
        return true;
      }
    }
  }

  // Filter errors from browser extension stack frames
  const frames = exceptionValues[0]?.stacktrace?.frames || [];
  if (frames.length > 0 && frames.every((frame) => /extensions\//i.test(frame.filename || ''))) {
    return true;
  }

  return false;
}

// Geocoding URLs carry the typed address in the path and the Mapbox token in the query.
export function redactBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
  const url = breadcrumb.data?.url;
  if (typeof url === 'string' && url.startsWith(MAPBOX_GEOCODING_URL_PREFIX)) {
    return {
      ...breadcrumb,
      data: { ...breadcrumb.data, url: `${MAPBOX_GEOCODING_URL_PREFIX}[redacted]` },
    };
  }
  return breadcrumb;
}

export function startSentry(dsn: string) {
  Sentry.init({
    dsn,
    integrations: [Sentry.browserTracingIntegration()],
    tracePropagationTargets: [/^https:\/\/[^/]*\.50skills\.(app|com|is|dev)(\/|$)/],
    tracesSampleRate: 0.05,
    environment: process.env.VITE_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
    release: process.env.__BUILD_REF__,
    beforeSend(event) {
      if (isIgnoredError(event)) {
        return null;
      }
      return event;
    },
    beforeSendTransaction(event) {
      const url = event.request?.url || event.transaction || '';
      if (STATIC_ASSET_PATTERN.test(url)) {
        return null;
      }
      return event;
    },
    beforeBreadcrumb: redactBreadcrumb,
  });
}

export function setSentryContext(key: string, data: Record<string, any>) {
  Sentry.setContext(key, data);
}
